import { ApplicationRef, inject, Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { concat, interval } from 'rxjs';
import { filter, first, switchMap, take } from 'rxjs/operators';
import { AlertService } from './alert.service';

@Injectable({
  providedIn: 'root',
})
export class UpdateService {
  private readonly update: SwUpdate = inject(SwUpdate);
  private readonly appRef: ApplicationRef = inject(ApplicationRef);
  private readonly alertService: AlertService = inject(AlertService);
  private readonly translate: TranslateService = inject(TranslateService);
  public reloadFn: () => void = () => window.location.reload();

  updateClient() {
    this.update.versionUpdates
      .pipe(
        filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'),
        switchMap(() => {
          const updateMessage = this.translate.instant('update_message');
          const updateTitle = this.translate.instant('new_updates_available');
          return this.alertService
            .confirmDialog$(updateTitle, {
              detail: updateMessage,
              hideCancel: true,
              disableClose: true,
              confirmText: this.translate.instant('lets_go'),
            })
            .pipe(take(1));
        }),
      )
      .subscribe(() => {
        this.activateUpdate();
      });
  }

  setupPeriodicUpdateCheck() {
    if (this.update.isEnabled) {
      this.updateClient();
      const appIsStable$ = this.appRef.isStable.pipe(first((isStable) => isStable === true));
      const everyThirtyMinutes$ = interval(1000 * 60 * 30);
      const everyThirtyMinutesOnceAppIsStable$ = concat(appIsStable$, everyThirtyMinutes$);

      everyThirtyMinutesOnceAppIsStable$.subscribe(() => {
        this.update.checkForUpdate().catch((err) => {
          console.error('Failed to check for updates:', err);
        });
      });
    } else {
      console.info('Service Worker is not enabled!');
    }
  }

  public activateUpdate() {
    this.update.activateUpdate().then(() => this.reloadFn());
  }
}
